import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ScoreGauge from "@/components/results/ScoreGauge";
import Timeline from "@/components/results/Timeline";
import ScenarioLab from "@/components/ScenarioLab";
import { compute } from "@/lib/calculations";
import { fmt$, fmtPct } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAnalysis, hasUserData } from "@/context/AnalysisContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, FileDown, LayoutGrid, Share2, Info, ArrowLeft, PencilLine, HelpCircle, ChevronDown, Lock, Sparkles } from "lucide-react";
import jsPDF from "jspdf";
import { track } from "@/lib/analytics";

const PREMIUM_BLOCK_MSG = "Home Buying Pass required — unlock 90 days for $29.99 (no auto-renewal).";

export default function Results() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const { inputs: ctxInputs, setCurrentStep, savedPropertyId, setSavedPropertyId } = useAnalysis();
  const [payload, setPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const isPremium = user?.entitlement?.plan_type === "premium";

  useEffect(() => {
    // Prefer the live worksheet from context. Fall back to session snapshot.
    if (hasUserData(ctxInputs)) {
      setPayload({ inputs: ctxInputs, results: compute(ctxInputs) });
      return;
    }
    const raw = sessionStorage.getItem("hs_current");
    if (!raw) { nav("/analyze"); return; }
    setPayload(JSON.parse(raw));
  }, [ctxInputs, nav]);

  // Fire analysis_completed once per Results mount when data is available.
  const completedRef = React.useRef(false);
  useEffect(() => {
    if (!completedRef.current && payload?.inputs?.financing_type) {
      completedRef.current = true;
      track("analysis_completed", { financing_type: payload.inputs.financing_type });
    }
  }, [payload]);

  const results = useMemo(() => payload ? compute(payload.inputs) : null, [payload]);
  if (!payload || !results) return null;
  const r = results;
  const isVA = payload.inputs.financing_type === "va";

  const goToStep = (idx) => { setCurrentStep(idx); nav(`/analyze?step=${idx}`); };
  const backToWizard = () => goToStep(5); // last wizard step

  const save = async () => {
    if (!user) {
      sessionStorage.setItem("hs_pending_save", "1");
      // Worksheet is persisted via AnalysisContext — no re-entry after login.
      nav("/login?next=/results");
      return;
    }
    setSaving(true);
    try {
      const nickname = prompt("Nickname for this home (e.g., '123 Maple St')") || "My Home";
      const { data } = await api.post("/properties", {
        nickname, financing_type: payload.inputs.financing_type,
        inputs: payload.inputs, results: r,
      });
      setSavedPropertyId(data.property_id);
      await refresh?.();
      track("home_saved", { financing_type: payload.inputs.financing_type });
      toast.success("Home saved to your dashboard");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response?.data?.detail || "Upgrade to save more homes.");
        nav("/upgrade?next=/results&reason=save_limit");
      } else {
        toast.error("Could not save home");
      }
    }
    setSaving(false);
  };

  const updateSave = async () => {
    if (!savedPropertyId) return;
    setSaving(true);
    try {
      await api.patch(`/properties/${savedPropertyId}`, {
        inputs: payload.inputs, results: r, financing_type: payload.inputs.financing_type,
      });
      toast.success("Saved home updated");
    } catch { toast.error("Could not update saved home"); }
    setSaving(false);
  };

  const exportPDF = async () => {
    if (!user) { nav("/login?next=/results"); return; }
    if (!isPremium) { nav("/upgrade?next=/results&reason=pdf_export"); return; }
    try {
      await api.post("/entitlement/verify?feature=pdf_export");
    } catch (e) {
      if (e?.response?.status === 402) { nav("/upgrade?next=/results&reason=pdf_export"); return; }
      toast.error("Could not verify access."); return;
    }
    // Text wordmark used pending delivery of Home Cost Path logo artwork.
    // When artwork lands, swap this block back to a `doc.addImage` call.
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const M = 48; let y = M;
    doc.setFont("helvetica", "bold"); doc.setFontSize(24); doc.setTextColor(30);
    doc.text("Home", M, y);
    const homeW = doc.getTextWidth("Home");
    doc.setTextColor(242, 109, 46); // brand accent
    doc.text("CostPath", M + homeW, y);
    doc.setTextColor(0); y += 28;
    doc.setFont("helvetica", "bold"); doc.setFontSize(18);
    doc.text("Purchase Analysis", M, y); y += 22;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120);
    doc.text("See the real cost before you buy.  ·  Generated " + new Date().toLocaleDateString(), M, y); y += 22;
    doc.setTextColor(0); doc.setFontSize(14); doc.setFont("helvetica","bold");
    doc.text("Property & Financing", M, y); y += 18;
    doc.setFont("helvetica","normal"); doc.setFontSize(11);
    const lines = [
      ["Financing type", payload.inputs.financing_type.toUpperCase()],
      ["Purchase price", fmt$(r.purchase_price)],
      ["Down payment", `${fmt$(r.down_payment_amount)} (${fmtPct(r.down_payment_pct)})`],
      ["Base loan", fmt$(r.base_loan)],
      ["VA funding fee", fmt$(r.funding_fee)],
      ["Total financed", fmt$(r.total_financed)],
      ["Interest rate / term", `${payload.inputs.interest_rate || 0}% / ${payload.inputs.term_years}yr`],
    ];
    lines.forEach(([k,v]) => { doc.text(`${k}:`, M, y); doc.text(String(v), M + 200, y); y += 16; });
    y += 8; doc.setFont("helvetica","bold"); doc.setFontSize(14); doc.text("Monthly Housing Cost", M, y); y += 18;
    doc.setFont("helvetica","normal"); doc.setFontSize(11);
    [
      ["Principal & interest", fmt$(r.principal_interest)],
      ["Property taxes", fmt$(r.taxes_monthly)],
      ["Insurance", fmt$(r.insurance_monthly)],
      ["HOA", fmt$(r.hoa_monthly)],
      ["Other housing", fmt$(r.other_housing_monthly)],
      ["Estimated true monthly", fmt$(r.monthly_total)],
    ].forEach(([k,v]) => { doc.text(`${k}:`, M, y); doc.text(String(v), M+200, y); y += 16; });
    y += 8; doc.setFont("helvetica","bold"); doc.setFontSize(14); doc.text("Cash Requirements", M, y); y += 18;
    doc.setFont("helvetica","normal"); doc.setFontSize(11);
    [
      ["Cash before closing", fmt$(r.cash_before_closing)],
      ["Cash at closing", fmt$(r.additional_cash_at_closing)],
      ["Max transaction cash", fmt$(r.max_transaction_cash)],
      ["Cash cushion after purchase", r.cash_remaining != null ? fmt$(r.cash_remaining) : "—"],
      ["Estimated DTI", r.dti != null ? fmtPct(r.dti) : "—"],
      ["Home Cost Path Purchase Score", r.score_available ? `${Math.round(r.score)} / 100` : "Not enough information yet"],
    ].forEach(([k,v]) => { doc.text(`${k}:`, M, y); doc.text(String(v), M+200, y); y += 16; });
    y += 12; doc.setFontSize(9); doc.setTextColor(120);
    const disc = "Home Cost Path is an independent educational planning tool provided by TaskHive LLC. Estimates are for planning only and are not loan offers, approvals, appraisals, or guarantees. Home Cost Path is not affiliated with or endorsed by the U.S. Department of Veterans Affairs.";
    doc.text(doc.splitTextToSize(disc, 500), M, y);
    doc.save("HomeCostPath-Report.pdf");
    toast.success("PDF exported");
  };

  const share = async () => {
    if (!user) { nav("/login?next=/results"); return; }
    if (!isPremium) { nav("/upgrade?next=/results&reason=share"); return; }
    try {
      const { data } = await api.post("/shared", {
        nickname: "Home Cost Path Analysis",
        financing_type: payload.inputs.financing_type,
        inputs: payload.inputs, results: r,
      });
      const url = `${window.location.origin}/shared/${data.token}`;
      try { await navigator.clipboard.writeText(url); track("report_shared"); toast.success("Share link copied to clipboard"); }
      catch { track("report_shared"); window.prompt("Copy this shareable link:", url); }
    } catch (e) {
      if (e?.response?.status === 402) { nav("/upgrade?next=/results&reason=share"); return; }
      toast.error("Could not create share link");
    }
  };

  return (
    <>
      <Nav />
      <main className="container-hs py-10 md:py-14">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <span className="badge-soft">Analysis Results</span>
            <h1 className="font-display text-3xl sm:text-4xl mt-2">Here's what this home may really cost</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="results-back" onClick={backToWizard} className="btn-secondary text-sm py-2 px-4"><ArrowLeft size={14}/> Back to wizard</button>
            {savedPropertyId ? (
              <button data-testid="update-save" onClick={updateSave} disabled={saving} className="btn-primary text-sm py-2 px-4"><Save size={14}/> {saving ? "Saving…" : "Update Saved Home"}</button>
            ) : (
              <button data-testid="save-home" onClick={save} disabled={saving} className="btn-secondary text-sm py-2 px-4"><Save size={14}/> Save Home</button>
            )}
            <button data-testid="share-report" onClick={share} className="btn-secondary text-sm py-2 px-4">{isPremium ? <Share2 size={14}/> : <Lock size={14}/>} Share</button>
            <button data-testid="export-pdf" onClick={exportPDF} className="btn-secondary text-sm py-2 px-4">{isPremium ? <FileDown size={14}/> : <Lock size={14}/>} Download PDF</button>
            <button data-testid="edit-analysis" onClick={() => goToStep(0)} className="btn-secondary text-sm py-2 px-4"><PencilLine size={14}/> Edit</button>
          </div>
        </div>

        {/* Review & Edit Inputs — jump to any wizard section without restarting */}
        <div data-testid="review-edit" className="card-hs p-5 mb-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display text-lg text-brand-ink">Review & Edit Inputs</div>
              <p className="text-xs text-brand-muted mt-0.5">Jump to any section — your other entries stay intact.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                ["Home", 1],
                ["Loan Details", 2],
                ["Financial Snapshot", 3],
                ["Closing Costs", 4],
                ["Pre-Closing Costs", 5],
              ].map(([label, idx]) => (
                <button key={idx} data-testid={`edit-step-${idx}`} onClick={() => goToStep(idx)}
                  className="rounded-full border border-brand-border bg-white px-3 py-1.5 text-xs text-brand-ink hover:border-brand-accent hover:bg-brand-soft/50">
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Hero: Maximum Transaction Cash */}
        <div data-testid="card-cash-max" className="card-hs p-8 sm:p-10 mb-6 bg-brand-soft/60 border-brand-accent/40">
          <div className="text-xs uppercase tracking-wider text-brand-muted">Estimated Maximum Cash Needed During This Purchase</div>
          <div className="num-xl mt-3 text-brand-accent">{fmt$(r.max_transaction_cash)}</div>
          <p className="text-sm text-brand-muted mt-3 max-w-2xl">The most cash you might need to have access to at any single moment while buying this home.{r.money_temporarily_tied_up > 0 ? " This includes earnest money that is temporarily out of your account even though it is expected to be applied as a credit toward amounts due at closing." : ""}</p>
        </div>

        {r.reserve_shortfall > 0 && (
          <div data-testid="reserve-shortfall-banner" className="mb-6 rounded-2xl border border-brand-warning/50 bg-brand-warning/10 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-warning/20 text-brand-warning"><Info size={16} /></span>
              <div className="flex-1">
                <div className="font-display text-lg text-brand-ink">Your cash cushion is below your desired reserve</div>
                <p className="text-sm text-brand-muted mt-1">After this purchase, Home Cost Path estimates about <b className="text-brand-ink">{fmt$(Math.max(r.cash_remaining || 0, 0))}</b> would remain — roughly <b className="text-brand-ink">{fmt$(r.reserve_shortfall)}</b> below your preferred reserve of <b className="text-brand-ink">{fmt$(r.desired_reserve)}</b>.</p>
                <p className="text-xs text-brand-muted mt-2">This is a planning signal, not a warning about approval. Consider negotiating seller/lender credits, adjusting earnest amount, or revisiting inspection priorities.</p>
              </div>
            </div>
          </div>
        )}

        {r.monthly_incomplete && (
          <div data-testid="missing-inputs-banner" className="mb-6 rounded-2xl border border-brand-warning/50 bg-brand-warning/10 p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-warning/20 text-brand-warning"><Info size={16} /></span>
              <div className="flex-1">
                <div className="font-display text-lg text-brand-ink">Estimated monthly housing cost is incomplete</div>
                <p className="text-sm text-brand-muted mt-1">The following inputs were left blank and are being treated as unknown, not $0:
                  {" "}
                  <b className="text-brand-ink">{r.missing_inputs.map(k => ({
                    interest_rate: "Interest Rate",
                    annual_taxes: "Annual Property Taxes",
                    annual_insurance: "Annual Homeowners Insurance",
                  })[k] || k).join(", ")}</b>.
                </p>
                <p className="text-xs text-brand-muted mt-2">Add these values in the wizard to get a complete estimate.</p>
              </div>
            </div>
          </div>
        )}

        {/* Supporting cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {(() => {
            const cushion = r.cash_remaining;
            const isShortfall = cushion != null && cushion < 0;
            const cards = [
              { k: "Cash Needed Before Closing", v: fmt$(r.cash_before_closing), sub: "Earnest, inspections, appraisal", t: "cash-before" },
              { k: "Additional Cash at Closing", v: fmt$(r.additional_cash_at_closing), sub: "After credits & earnest credit", t: "cash-at" },
              { k: "Estimated True Monthly Housing Cost", v: fmt$(r.monthly_total), sub: "PITI + HOA + other", t: "monthly-total" },
              isShortfall
                ? { k: "Estimated Cash Shortfall", v: fmt$(Math.abs(cushion)), sub: "Cash needed beyond what's available", t: "cash-shortfall", shortfall: true }
                : { k: "Estimated Cash Cushion After Purchase", v: cushion != null ? fmt$(cushion) : "—", sub: r.cash_available ? "Based on cash available you entered" : "Enter cash available to see this", t: "cash-cushion" },
            ];
            return cards.map(c => (
              <div key={c.k} data-testid={`card-${c.t}`} className={`card-hs p-6 ${c.shortfall ? "border-brand-danger/50 bg-brand-danger/5" : ""}`}>
                <div className="text-xs uppercase tracking-wider text-brand-muted">{c.k}</div>
                <div className={`num-lg mt-3 ${c.shortfall ? "text-brand-danger" : ""}`}>{c.v}</div>
                <div className="text-xs text-brand-muted mt-2">{c.sub}</div>
              </div>
            ));
          })()}
        </div>

        {/* Score + Plain english */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="card-hs p-6 lg:col-span-1">
            {r.score_available ? (
              <>
                <ScoreGauge score={r.score} />
                <div className="mt-4 space-y-2">
                  {r.score_reasons.strengths.map((s) => <div key={`s:${s}`} className="text-xs text-brand-success flex gap-2"><span>•</span>{s}</div>)}
                  {r.score_reasons.watch.map((s) => <div key={`w:${s}`} className="text-xs text-brand-warning flex gap-2"><span>•</span>{s}</div>)}
                </div>
                <details data-testid="score-how" className="mt-3">
                  <summary className="text-xs text-brand-accent cursor-pointer inline-flex items-center gap-1 select-none"><HelpCircle size={12}/> How is this score calculated?</summary>
                  <div className="text-xs text-brand-muted mt-2 leading-relaxed">
                    Home Cost Path looks only at the financial information you've entered:
                    <ul className="list-disc ml-5 mt-1 space-y-0.5">
                      <li>Estimated debt-to-income ratio</li>
                      <li>Cash available vs. peak transaction cash</li>
                      <li>Estimated post-purchase cash cushion vs. your desired reserve</li>
                      <li>How much of your closing costs are offset by credits</li>
                      <li>Whether required inputs are complete</li>
                    </ul>
                    <p className="mt-2">This is not a lender or VA underwriting score.</p>
                  </div>
                </details>
                <p className="text-xs text-brand-muted mt-4">Educational planning score based only on the financial information entered into Home Cost Path. It does not predict loan approval, VA approval, appraisal outcome, property condition, creditworthiness, or underwriting.</p>
              </>
            ) : (
              <div data-testid="score-unavailable" className="py-4">
                <div className="text-xs uppercase tracking-wider text-brand-muted">Home Cost Path Purchase Score</div>
                <div className="font-display text-2xl mt-2 text-brand-muted">Not enough information yet</div>
                <p className="text-sm text-brand-muted mt-3">Complete the home and financial details to generate a Home Cost Path Purchase Score. Add a purchase price, interest rate, and either income or cash available.</p>
              </div>
            )}
          </div>
          <div className="card-hs p-6 lg:col-span-2">
            <h2 className="font-display text-2xl mb-4">Here's the Important Part</h2>
            <div className="space-y-3 text-brand-ink leading-relaxed">
              {r.cash_before_closing > 0
                ? <p>Before closing day, you may need about <b>{fmt$(r.cash_before_closing)}</b> ready to spend on things like earnest money, inspections, and the appraisal.</p>
                : <p>No pre-closing expenses were entered in this scenario, so the peak cash need occurs at closing.</p>}
              {r.money_temporarily_tied_up > 0 ? (
                <p>
                  Of that, roughly <b>{fmt$(r.money_temporarily_tied_up)}</b> is <b className="text-brand-ink">temporarily out of your account</b> — earnest money that is expected to be applied as a credit toward amounts due at closing. The other <b>{fmt$(r.money_likely_spent)}</b> is money that will likely be <b className="text-brand-ink">permanently spent</b> (inspections, appraisal, and similar fees).
                </p>
              ) : r.money_likely_spent > 0 ? (
                <p>Those pre-closing expenses of <b>{fmt$(r.money_likely_spent)}</b> are generally <b>permanently spent</b> — inspections and appraisal fees are not credited back at closing.</p>
              ) : null}
              <p>On closing day, plan to bring about <b>{fmt$(r.additional_cash_at_closing)}</b> more (down payment, remaining closing costs, prepaid escrow — minus any seller or lender credits and any earnest money credit).</p>
              {r.money_temporarily_tied_up > 0
                ? <p>Because your earnest money leaves your account before it is applied as a credit toward closing amounts due, the <b>most cash you may need access to at any single point</b> during the transaction is about <b className="text-brand-accent">{fmt$(r.max_transaction_cash)}</b>. That is Home Cost Path's peak-cash number — not the total permanently spent.</p>
                : <p>The <b>most cash you may need access to at any single point</b> during this transaction is about <b className="text-brand-accent">{fmt$(r.max_transaction_cash)}</b>. In this scenario, the peak occurs at {r.cash_before_closing > r.additional_cash_at_closing ? "the pre-closing stage" : "closing"}.</p>}
              {r.cash_available > 0 && r.cash_remaining != null && (
                r.cash_remaining < 0
                  ? <p>After completing the purchase, Home Cost Path estimates a <b className="text-brand-danger">cash shortfall of {fmt$(Math.abs(r.cash_remaining))}</b>{r.desired_reserve ? <> — well below your preferred reserve of <b>{fmt$(r.desired_reserve)}</b></> : null}.</p>
                  : <p>Once the dust settles, Home Cost Path estimates you would have roughly <b>{fmt$(Math.max(r.cash_remaining, 0))}</b> left as a cash cushion{r.desired_reserve ? <> — compared with your preferred reserve of <b>{fmt$(r.desired_reserve)}</b>{r.reserve_shortfall > 0 ? <>, about <b>{fmt$(r.reserve_shortfall)}</b> below what you'd like to keep on hand</> : <>, which meets or exceeds your preferred reserve</>}</> : null}.</p>
              )}
              {r.cash_gap > 0 && <p className="rounded-xl bg-brand-warning/10 border border-brand-warning/30 p-4 text-sm">This scenario may require more cash than you currently have available (gap: <b>{fmt$(r.cash_gap)}</b>). Consider discussing seller credits, lender credits, earnest-money amount, inspection priorities, or other legitimate options with your lender and real-estate professional.</p>}
            </div>

            {/* Spent vs tied-up breakdown */}
            {(r.money_likely_spent > 0 || r.money_temporarily_tied_up > 0) && (
              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <div className="rounded-xl bg-brand-bg p-4 border border-brand-border">
                  <div className="text-xs text-brand-muted">Money Likely Spent</div>
                  <div className="text-xl font-semibold mt-1 text-brand-ink" data-testid="money-likely-spent">{fmt$(r.money_likely_spent)}</div>
                  <div className="text-xs text-brand-muted mt-1">Inspection, appraisal, non-creditable fees</div>
                </div>
                <div className="rounded-xl bg-brand-bg p-4 border border-brand-border">
                  <div className="text-xs text-brand-muted">Money Temporarily Tied Up</div>
                  <div className="text-xl font-semibold mt-1 text-brand-ink" data-testid="money-tied-up">{fmt$(r.money_temporarily_tied_up)}</div>
                  <div className="text-xs text-brand-muted mt-1">Earnest money expected to be applied as a credit toward closing amounts</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Closing-cost breakdown — plain language */}
        {(r.closing_costs > 0 || r.total_credits > 0 || r.earnest_credited_at_closing > 0 || r.down_payment_amount > 0) && (
          <div data-testid="closing-breakdown" className="card-hs p-6 mb-8">
            <h3 className="font-display text-xl mb-4">Closing-day accounting</h3>
            <div className="space-y-1 text-sm">
              <RowSigned k="Estimated closing costs" v={r.closing_costs} />
              <RowSigned k="Prepaids / escrow" v={r.prepaids} hideZero />
              <RowSigned k="Discount points / buydown" v={r.discount_points} hideZero />
              <RowSigned k="Other closing expense" v={r.other_closing} hideZero />
              <RowSigned k="Down payment" v={r.down_payment_amount} hideZero />
              <RowSigned k="Seller contribution" v={-Number(payload.inputs.seller_credit || 0)} isCredit hideZero />
              <RowSigned k="Lender credit" v={-Number(payload.inputs.lender_credit || 0)} isCredit hideZero />
              <RowSigned k="Other credit" v={-Number(payload.inputs.other_credit || 0)} isCredit hideZero />
              {r.earnest_credited_at_closing > 0 && (
                <RowSigned k="Earnest money applied at closing" v={-r.earnest_credited_at_closing} isCredit />
              )}
              <div className="border-t border-brand-border mt-2 pt-2 flex justify-between font-semibold">
                <span>Additional cash needed at closing</span>
                <span className="text-brand-accent">{fmt$(r.additional_cash_at_closing)}</span>
              </div>
            </div>
            <p className="text-xs text-brand-muted mt-3">Credits reduce cash needed at closing; they don't reduce your total price. Home Cost Path treats the seller contribution as a planning allocation; your lender determines allowable treatment.</p>
          </div>
        )}

        {/* Breakdown */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="card-hs p-6">
            <h3 className="font-display text-xl mb-4">True Monthly Housing Cost</h3>
            <Row k="Principal & Interest" v={fmt$(r.principal_interest)} />
            <Row k="Property Taxes" v={fmt$(r.taxes_monthly)} />
            <Row k="Homeowners Insurance" v={fmt$(r.insurance_monthly)} />
            <Row k="HOA" v={fmt$(r.hoa_monthly)} />
            <Row k="Other Recurring" v={fmt$(r.other_housing_monthly)} />
            <div className="border-t border-brand-border mt-3 pt-3 flex justify-between font-semibold">
              <span>Estimated True Monthly</span><span className="text-brand-accent">{fmt$(r.monthly_total)}</span>
            </div>
          </div>
          {isVA && (
            <div className="card-hs p-6">
              <h3 className="font-display text-xl mb-4">VA Loan Summary</h3>
              <Row k="Purchase Price" v={fmt$(r.purchase_price)} />
              <Row k="Down Payment" v={`${fmt$(r.down_payment_amount)} (${fmtPct(r.down_payment_pct)})`} />
              <Row k="Base Loan Amount" v={fmt$(r.base_loan)} />
              <Row k="Estimated VA Funding Fee" v={fmt$(r.funding_fee)} />
              <Row k="Funding Fee Exemption" v={payload.inputs.va_exempt === "yes" ? "Exempt" : payload.inputs.va_exempt === "not_sure" ? "Unsure" : "Not exempt"} />
              <Row k="Total Financed" v={fmt$(r.total_financed)} />
              <Row k="Interest Rate / Term" v={`${payload.inputs.interest_rate || 0}% / ${payload.inputs.term_years}yr`} />
              <Row k="Estimated P&I Payment" v={fmt$(r.principal_interest)} />
            </div>
          )}
        </div>

        {/* Affordability if income provided */}
        {r.dti != null && (
          <div className="card-hs p-6 mb-8">
            <h3 className="font-display text-xl mb-4">Affordability Snapshot</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Stat k="Gross Monthly Income" v={fmt$(Number(payload.inputs.monthly_income))} />
              <Stat k="Monthly Debt" v={fmt$(r.monthly_debts)} />
              <Stat k="Est. Housing Payment" v={fmt$(r.monthly_total)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div data-testid="ratio-housing" className="rounded-xl bg-brand-bg p-4 border border-brand-border">
                <div className="text-xs text-brand-muted">Estimated Housing-to-Income Ratio</div>
                <div className="text-2xl font-semibold mt-1 text-brand-ink">{fmtPct(r.housing_to_income_ratio)}</div>
                <div className="text-xs text-brand-muted mt-1">Housing payment ÷ gross monthly income</div>
              </div>
              <div data-testid="ratio-total-dti" className="rounded-xl bg-brand-bg p-4 border border-brand-border">
                <div className="text-xs text-brand-muted">Estimated Total Debt-to-Income Ratio</div>
                <div className="text-2xl font-semibold mt-1 text-brand-ink">{fmtPct(r.dti)}</div>
                <div className="text-xs text-brand-muted mt-1">(All monthly debts + housing) ÷ gross monthly income</div>
              </div>
            </div>
            <p className="text-xs text-brand-muted mt-4">Lender calculations may differ. These ratios are for planning purposes only and do not indicate approval.</p>
            {isVA && (
              <div data-testid="va-residual-note" className="mt-4 rounded-xl border border-brand-border bg-brand-bg p-4 text-sm">
                <div className="font-medium text-brand-ink">VA underwriting looks at more than DTI</div>
                <p className="text-xs text-brand-muted mt-1 leading-relaxed">VA lenders may consider residual income in addition to debt-to-income ratio. Residual income generally reflects money remaining after certain major monthly obligations and varies based on factors such as household size, region, and loan amount. Home Cost Path's DTI estimate is for planning only and does not represent an underwriting decision.</p>
                <span className="badge-soft mt-2">Residual Income Calculator — Coming Soon</span>
              </div>
            )}
          </div>
        )}

        {isVA && (
          <div data-testid="va-things-to-know" className="card-hs p-6 mb-8">
            <details>
              <summary className="cursor-pointer font-display text-xl text-brand-ink inline-flex items-center gap-2 select-none">
                VA Things to Know <ChevronDown size={16} className="text-brand-muted" />
              </summary>
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <VaFact title="VA appraisal is not a home inspection">A VA appraisal helps establish the property's value and evaluates applicable VA property requirements. It is not a substitute for an independent home inspection.</VaFact>
                <VaFact title="Appraised value matters">VA purchase contracts may include the VA Escape Clause, which can provide protection if the VA-established reasonable value is below the purchase price. Your lender and real-estate professional can explain how this applies to your contract.
                  <details className="mt-2"><summary className="text-xs text-brand-accent cursor-pointer" data-testid="escape-clause">What is the VA Escape Clause?</summary>
                    <p className="text-xs text-brand-muted mt-1">The VA Escape Clause may allow a VA buyer to avoid being required to complete the purchase solely because the VA-established reasonable value is below the contract price. Contract terms and timing matter. Discuss your specific purchase agreement with your lender and real-estate professional.</p>
                  </details>
                </VaFact>
                <VaFact title="The property must meet VA requirements">Certain property conditions identified during the VA appraisal may need to be addressed before the loan can close. Requirements can vary by property and location.</VaFact>
                <VaFact title="$0 down does not necessarily mean $0 cash">Even with a zero-down VA loan, buyers may need cash for earnest money, inspections, appraisal fees, prepaid taxes and insurance, escrow funding, discount points, or other transaction costs.</VaFact>
                <VaFact title="Eligibility is not loan approval">VA home-loan eligibility or entitlement does not guarantee approval for a specific loan amount. The lender makes the final credit, income, debt, asset, property, and underwriting decision.</VaFact>
                <VaFact title="Primary residence">VA purchase loans are generally intended for a home the eligible borrower plans to occupy as a primary residence, subject to VA occupancy rules.</VaFact>
              </div>
            </details>
          </div>
        )}

        {isVA && (
          <div data-testid="va-disclaimer" className="text-xs text-brand-muted rounded-2xl border border-brand-border bg-brand-soft/40 p-5 mb-8 leading-relaxed">
            Home Cost Path is not affiliated with or endorsed by the U.S. Department of Veterans Affairs. VA eligibility, entitlement, funding-fee status, property requirements, allowable charges, loan terms, and final approval must be confirmed by VA and/or your lender as applicable.
          </div>
        )}

        {/* Timeline */}
        <div className="card-hs p-6 mb-8">
          <h3 className="font-display text-xl mb-6">Home-Buying Cash Timeline</h3>
          <Timeline preItems={payload.inputs.pre_closing_items || []} additionalCashAtClosing={r.additional_cash_at_closing} />
        </div>

        {/* MORE VA TOOLS — premium-locked shells (fully built in Build 2) */}
        {isVA && (
          <div data-testid="more-va-tools" className="card-hs p-6 mb-8">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs uppercase tracking-wider text-brand-muted font-semibold">More VA Tools</span>
              {!isPremium && <span className="text-[10px] uppercase tracking-wider text-brand-accent font-semibold bg-brand-soft rounded-full px-2 py-0.5">Premium</span>}
            </div>
            <p className="text-sm text-brand-muted mb-4">Optional planning tools built specifically for VA buyers.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button data-testid="va-tool-residual"
                onClick={() => isPremium ? toast.info("Residual Income Planner — arriving soon.") : nav("/upgrade?next=/results&reason=va_residual")}
                className="text-left rounded-2xl border border-brand-border p-4 hover:border-brand-accent transition-colors bg-white">
                <div className="flex items-center gap-2 text-brand-ink font-medium">
                  {isPremium ? <Sparkles size={14} className="text-brand-accent" /> : <Lock size={14} className="text-brand-muted" />}
                  Residual Income Planner
                </div>
                <div className="text-xs text-brand-muted mt-1">Estimate residual income against VA planning tables (household size + region).</div>
                <div className="text-[11px] text-brand-muted mt-2">{isPremium ? "Included with your pass · Coming soon" : "Unlock 90 days for $29.99"}</div>
              </button>
              <button data-testid="va-tool-gap"
                onClick={() => isPremium ? toast.info("Appraisal Gap Calculator — arriving soon.") : nav("/upgrade?next=/results&reason=va_gap")}
                className="text-left rounded-2xl border border-brand-border p-4 hover:border-brand-accent transition-colors bg-white">
                <div className="flex items-center gap-2 text-brand-ink font-medium">
                  {isPremium ? <Sparkles size={14} className="text-brand-accent" /> : <Lock size={14} className="text-brand-muted" />}
                  Appraisal Gap Calculator
                </div>
                <div className="text-xs text-brand-muted mt-1">Plan the cash you may need if a VA appraisal comes in below the contract price.</div>
                <div className="text-[11px] text-brand-muted mt-2">{isPremium ? "Included with your pass · Coming soon" : "Unlock 90 days for $29.99"}</div>
              </button>
            </div>
          </div>
        )}

        {/* Scenario Lab */}
        <div className="card-hs p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <LayoutGrid className="text-brand-accent" size={20} />
            <h3 className="font-display text-xl">Offer Scenario Lab</h3>
          </div>
          <ScenarioLab baseInputs={payload.inputs} />
        </div>
      </main>
      <Footer />
    </>
  );
}

function Row({ k, v }) { return <div className="flex justify-between py-2 text-sm"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-ink">{v}</span></div>; }
function RowSigned({ k, v, isCredit, hideZero }) {
  if (hideZero && !v) return null;
  const val = fmt$(Math.abs(v || 0));
  return (
    <div className="flex justify-between py-1">
      <span className="text-brand-muted">{k}</span>
      <span className={isCredit ? "font-medium text-brand-success" : "font-medium text-brand-ink"}>
        {isCredit ? "−" : ""}{val}
      </span>
    </div>
  );
}
function Stat({ k, v, accent }) { return <div className="rounded-xl bg-brand-bg p-4"><div className="text-xs text-brand-muted">{k}</div><div className={`text-xl font-semibold mt-1 ${accent ? "text-brand-accent" : "text-brand-ink"}`}>{v}</div></div>; }
function VaFact({ title, children }) {
  return (
    <div className="rounded-xl border border-brand-border bg-brand-bg p-4">
      <div className="font-medium text-brand-ink text-sm">{title}</div>
      <div className="text-xs text-brand-muted mt-1 leading-relaxed">{children}</div>
    </div>
  );
}
